import { supabase } from '../../../lib/supabaseClient';
import { BLUEPRINTS_TABLE } from './blueprintDataHelpers';

export interface BlueprintMetricSummary {
  total: number;
  published: number;
  standards: number;
  archived: number;
  active: number;
  usageTotal: number;
}

export interface BlueprintUsageItem {
  blueprint_id: string;
  blueprint_name: string;
  count: number;
}

export async function getBlueprintMetrics(organizationId: string): Promise<BlueprintMetricSummary> {
  const { data, error } = await supabase
    .from(BLUEPRINTS_TABLE)
    .select('id, status, is_corporate_standard')
    .eq('organization_id', organizationId);
  if (error) throw error;

  const total = data?.length ?? 0;
  const published = data?.filter((b) => b.status === 'published')?.length ?? 0;
  const standards = data?.filter((b) => b.is_corporate_standard)?.length ?? 0;
  const archived = data?.filter((b) => b.status === 'archived')?.length ?? 0;
  const active = total - archived;

  let usageCount = 0;
  try {
    const result = await supabase
      .from('process_blueprint_usage')
      .select('id', { count: 'exact' })
      .eq('organization_id', organizationId);
    if (!result.error) usageCount = result.count ?? 0;
  } catch {
    // Table may not exist yet
  }

  return {
    total,
    published,
    standards,
    archived,
    active,
    usageTotal: usageCount,
  };
}

export async function getTopUsedBlueprints(organizationId: string, limit = 5): Promise<BlueprintUsageItem[]> {
  let data: any[] | null = null;
  try {
    const result = await supabase
      .from('process_blueprint_usage')
      .select('blueprint_id')
      .eq('organization_id', organizationId);
    if (!result.error) data = result.data;
  } catch {
    // Table may not exist yet
  }
  if (!data || data.length === 0) return [];

  const counts = new Map<string, number>();
  data.forEach((row) => {
    const id = row.blueprint_id as string | null;
    if (!id) return;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  });

  const sortedEntries = Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);

  if (sortedEntries.length === 0) return [];

  const blueprintIds = sortedEntries.map(([id]) => id);
  const { data: blueprints, error: bpError } = await supabase
    .from(BLUEPRINTS_TABLE)
    .select('id, name')
    .in('id', blueprintIds);
  if (bpError) throw bpError;

  const nameMap = new Map((blueprints ?? []).map((bp) => [bp.id, bp.name]));

  return sortedEntries.map(([id, count]) => ({
    blueprint_id: id,
    blueprint_name: nameMap.get(id) ?? 'Blueprint',
    count,
  }));
}

export async function getUsageForBlueprint(blueprintId: string, organizationId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('process_blueprint_usage')
      .select('id', { count: 'exact' })
      .eq('blueprint_id', blueprintId)
      .eq('organization_id', organizationId);
    if (error) return 0;
    return count ?? 0;
  } catch {
    return 0;
  }
}
